/**
 * 처방 자동 OCR 매칭 통계.
 * admin-ruda-web `OcrMatchStatsCard.js` 포팅.
 * `panel`: 필터 옆 7:3 배치용 — 카드 높이 채움 + 세로 스택.
 */
import type { OcrMatchStats } from '@/entities/prescription/api/prescription.api';
import { cn } from '@/shared/lib/utils';
import React from 'react';

interface OcrMatchStatsCardProps {
  stats?: OcrMatchStats | null;
  className?: string;
  /** card: 단독 / panel: 필터 옆 패널(세로) */
  variant?: 'card' | 'panel';
}

const StatItem: React.FC<{
  label: string;
  value: number;
  valueClassName?: string;
  stacked?: boolean;
}> = ({ label, value, valueClassName, stacked }) => (
  <div
    className={cn(
      'flex items-center justify-center',
      stacked
        ? 'w-full flex-col gap-1 py-1'
        : 'w-[7.5rem] flex-col gap-1.5',
    )}
  >
    <span className="text-sm text-muted-foreground">{label}</span>
    <span
      className={cn(
        'font-semibold leading-none tabular-nums text-foreground',
        stacked ? 'text-2xl' : 'text-3xl',
        valueClassName,
      )}
    >
      {value.toLocaleString('ko-KR')}
    </span>
  </div>
);

export const OcrMatchStatsCard: React.FC<OcrMatchStatsCardProps> = ({
  stats,
  className,
  variant = 'card',
}) => {
  if (!stats) return null;

  const { total = 0, waiting = 0, completed = 0 } = stats;
  const stacked = variant === 'panel';

  return (
    <div
      className={cn(
        'flex shrink-0',
        stacked
          ? 'h-full w-full flex-col items-stretch justify-center gap-2 rounded-xl border border-border bg-card px-4 py-5 shadow-sm'
          : 'inline-flex items-stretch rounded-xl border border-border bg-card p-5 shadow-sm',
        className,
      )}
      aria-label="자동 매칭 통계"
    >
      <StatItem label="전체 이미지 수" value={total} stacked={stacked} />
      <div
        className={cn(
          'bg-border',
          stacked ? 'mx-2 h-px w-auto' : 'mx-1 w-px self-stretch',
        )}
      />
      <StatItem label="매칭 대기" value={waiting} stacked={stacked} />
      <div
        className={cn(
          'bg-border',
          stacked ? 'mx-2 h-px w-auto' : 'mx-1 w-px self-stretch',
        )}
      />
      <StatItem
        label="매칭 완료"
        value={completed}
        valueClassName="text-ruda-error"
        stacked={stacked}
      />
    </div>
  );
};
